/**
 * VendorPlaceholder - A dynamic placeholder for listings without photos
 * Displays the vendor/listing name with a nice font and light gradient background
 */

// Generate a consistent gradient based on the vendor name
const getGradientForName = (name) => {
  if (!name) return { from: '#e8f5e9', to: '#c8e6c9' }; // Default green tones
  
  // Simple hash function to get consistent colors from name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Define pleasant, light gradient pairs
  const gradients = [
    { from: '#e8f5e9', to: '#c8e6c9' }, // Soft green
    { from: '#e3f2fd', to: '#bbdefb' }, // Soft blue
    { from: '#fff3e0', to: '#ffe0b2' }, // Soft orange
    { from: '#f3e5f5', to: '#e1bee7' }, // Soft purple
    { from: '#e0f7fa', to: '#b2ebf2' }, // Soft teal
    { from: '#fce4ec', to: '#f8bbd9' }, // Soft pink
    { from: '#f1f8e9', to: '#dcedc8' }, // Soft lime
    { from: '#e8eaf6', to: '#c5cae9' }, // Soft indigo
  ];
  
  const index = Math.abs(hash) % gradients.length;
  return gradients[index];
};

// Get initials from name (up to 2 characters)
const getInitials = (name) => {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
};

export const VendorPlaceholder = ({ 
  name, 
  className = '', 
  showInitials = true,
  size = 'default' // 'small', 'default', 'large'
}) => {
  const gradient = getGradientForName(name);
  const initials = getInitials(name);
  const displayName = name || 'Vendor';
  
  // Size configurations
  const sizeConfig = {
    small: {
      initialsSize: 'text-2xl',
      nameSize: 'text-xs',
      padding: 'p-2',
    },
    default: {
      initialsSize: 'text-4xl md:text-5xl',
      nameSize: 'text-sm md:text-base',
      padding: 'p-4',
    },
    large: {
      initialsSize: 'text-5xl md:text-6xl',
      nameSize: 'text-base md:text-lg',
      padding: 'p-6',
    },
  };
  
  const config = sizeConfig[size] || sizeConfig.default;
  
  return (
    <div 
      className={`w-full h-full flex flex-col items-center justify-center ${config.padding} ${className}`}
      style={{
        background: `linear-gradient(135deg, ${gradient.from} 0%, ${gradient.to} 100%)`,
      }}
      data-testid="vendor-placeholder"
    >
      {showInitials && (
        <div 
          className={`${config.initialsSize} font-bold text-stone-600/70 mb-2`}
          style={{ fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}
        >
          {initials}
        </div>
      )}
      <div 
        className={`${config.nameSize} font-semibold text-stone-700/80 text-center line-clamp-2 max-w-[90%]`}
        style={{ fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}
      >
        {displayName}
      </div>
    </div>
  );
};

export default VendorPlaceholder;
